import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import CheckIcon from '@mui/icons-material/Check';
import ReplayIcon from '@mui/icons-material/Replay';
import { useTranslation } from 'react-i18next';
import { useBudget } from '../../contexts/BudgetContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useThemeContext } from '../../contexts/ThemeContext';
import { voiceApi } from '../../services/api';
import api from '../../services/api';

const INTENT_LABEL = {
  transaction: 'Transaction',
  pantry: 'Pantry',
  payable: 'Payable',
  unknown: 'Command',
};

function pickRecorderMime() {
  if (typeof MediaRecorder === 'undefined') return '';
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

export default function DashboardVoiceCapture() {
  const { t, i18n } = useTranslation();
  const { themeMode } = useThemeContext();
  const isLight = themeMode !== 'dark';
  const { categories = [], handleSaveTransaction } = useBudget();
  const { hasFeature } = useSubscription();

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const maxTimerRef = useRef(null);

  const [listening, setListening] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [form, setForm] = useState({});
  const [sheetOpen, setSheetOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [typed, setTyped] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const parseText = useCallback(async (text) => {
    setParsing(true);
    try {
      const result = await voiceApi.parseCommand(text, i18n.language);
      if (result?.success && result.parsed) {
        setParsed(result.parsed);
        setForm({
          intent: result.parsed.intent || 'transaction',
          action: result.parsed.action || 'create',
          type: result.parsed.type || 'expense',
          amount: result.parsed.amount ?? '',
          description: result.parsed.description || result.parsed.name || '',
          name: result.parsed.name || result.parsed.description || '',
          categoryId: result.parsed.categoryId || categories[0]?.id || categories[0]?._id || '',
          date: result.parsed.date || new Date().toISOString().slice(0, 10),
          dueDay: result.parsed.dueDay || new Date().getDate(),
          payableId: result.parsed.payableId || '',
          pantryId: result.parsed.pantryId || '',
          status: result.parsed.status || result.parsed.action || 'need',
        });
        setSheetOpen(true);
      } else {
        setSnack({ open: true, message: t('voice.couldNotUnderstand', 'Could not understand. Try again.'), severity: 'warning' });
      }
    } catch (_) {
      setSnack({ open: true, message: t('voice.couldNotUnderstand', 'Could not understand. Try again.'), severity: 'error' });
    } finally {
      setParsing(false);
    }
  }, [i18n.language, t, categories]);

  const stopTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    }
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
  };

  const finishRecording = useCallback(async (blob) => {
    if (!blob || blob.size < 600) {
      setListening(false);
      setSnack({ open: true, message: t('voice.noSpeech', 'No speech detected. Try again or type it.'), severity: 'warning' });
      return;
    }
    setParsing(true);
    try {
      const result = await voiceApi.transcribe(blob, i18n.language);
      const text = result?.text || result?.data?.text;
      if (!text) throw new Error('empty');
      await parseText(text);
    } catch (_) {
      setSnack({ open: true, message: t('voice.sttFailed', 'Could not hear that. Type it below.'), severity: 'warning' });
    } finally {
      setParsing(false);
      setListening(false);
    }
  }, [i18n.language, parseText, t]);

  const stopRecording = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try { rec.stop(); } catch (_) { /* already stopped */ }
    } else {
      stopTracks();
      setListening(false);
    }
  }, []);

  const start = useCallback(async () => {
    setParsed(null);
    setTyped('');
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setListening(true);
      setSnack({ open: true, message: t('voice.typeInstead', 'Mic unavailable here — type the command.'), severity: 'info' });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickRecorderMime();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        stopTracks();
        finishRecording(blob);
        recorderRef.current = null;
      };
      recorderRef.current = rec;
      rec.start(200);
      setListening(true);
      maxTimerRef.current = setTimeout(() => stopRecording(), 12000);
    } catch (err) {
      setListening(true);
      const denied = err && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError');
      setSnack({
        open: true,
        message: denied
          ? t('voice.micDenied', 'Microphone blocked. Type the command instead.')
          : t('voice.typeInstead', 'Mic unavailable here — type the command.'),
        severity: 'warning',
      });
    }
  }, [finishRecording, stopRecording, t]);

  useEffect(() => {
    const onStart = () => start();
    window.addEventListener('sk:voice-start', onStart);
    return () => window.removeEventListener('sk:voice-start', onStart);
  }, [start]);

  useEffect(() => () => {
    stopTracks();
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch (_) { /* */ }
    }
  }, []);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const intent = form.intent;
      if (intent === 'pantry') {
        if (form.pantryId) {
          await api.patch(`/pantry/${form.pantryId}`, { status: form.status === 'have' ? 'have' : 'need' });
        } else {
          await api.post('/pantry', { name: form.name || form.description, status: form.status === 'have' ? 'have' : 'need' });
        }
        setSnack({ open: true, message: t('voice.savedPantry', 'Pantry updated'), severity: 'success' });
      } else if (intent === 'payable') {
        if (form.action === 'mark_paid' && form.payableId) {
          await api.post(`/payables/${form.payableId}/pay`, {
            amount: form.amount || undefined,
            categoryId: form.categoryId || undefined,
          });
          setSnack({ open: true, message: t('voice.savedPaid', 'Marked as paid'), severity: 'success' });
        } else {
          await api.post('/payables', {
            name: form.name || form.description,
            amount: Number(form.amount) || 0,
            dueDay: Number(form.dueDay) || 1,
            kind: 'bill',
            categoryId: form.categoryId || undefined,
          });
          setSnack({ open: true, message: t('voice.savedPayable', 'Bill added to Payables'), severity: 'success' });
        }
      } else {
        const payload = {
          type: form.type || 'expense',
          amount: Number(form.amount) || 0,
          description: form.description || form.name,
          date: form.date,
          budgetMonth: form.date ? String(form.date).slice(0, 7) : undefined,
        };
        if (payload.type === 'expense') payload.categoryId = form.categoryId;
        await handleSaveTransaction(payload);
        setSnack({ open: true, message: t('voice.savedTx', 'Transaction saved'), severity: 'success' });
      }
      setSheetOpen(false);
    } catch (err) {
      const locked = err?.response?.data?.code === 'FEATURE_NOT_AVAILABLE';
      setSnack({
        open: true,
        message: locked
          ? t('voice.premiumRequired', 'That feature needs Premium')
          : (err?.response?.data?.message || t('voice.saveFailed', 'Could not save')),
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const intent = form.intent || parsed?.intent || 'transaction';
  const canSave = intent === 'pantry'
    ? Boolean(form.name || form.description)
    : Boolean(Number(form.amount) > 0 && (form.description || form.name));

  const overlayOn = (listening || parsing) && !sheetOpen;

  return (
    <>
      <Fade in={overlayOn}>
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400,
            display: overlayOn ? 'flex' : 'none',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isLight ? 'rgba(255,255,255,0.94)' : 'rgba(0,0,0,0.88)',
            backdropFilter: 'blur(10px)',
            px: 2,
          }}
        >
          <Box
            onClick={listening && !parsing ? stopRecording : undefined}
            sx={{
              width: 112, height: 112, borderRadius: '50%', mb: 2, cursor: listening ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: listening ? 'error.main' : 'primary.main',
            }}
          >
            {parsing ? <CircularProgress sx={{ color: '#fff' }} /> : <MicIcon sx={{ fontSize: 44, color: '#fff' }} />}
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {parsing
              ? t('voice.processing', 'Processing...')
              : t('voice.listening', 'Listening... tap to stop')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            {t('voice.speakHint', 'Expense, income, “we’re out of milk”, or “I paid the rent”')}
          </Typography>
          <Box sx={{ width: '100%', maxWidth: 360 }} onClick={(e) => e.stopPropagation()}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('voice.typePlaceholder', 'Or type it here and press Enter')}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && typed.trim().length >= 3) {
                  e.preventDefault();
                  stopRecording();
                  parseText(typed.trim());
                  setListening(false);
                }
              }}
            />
          </Box>
          <Button sx={{ mt: 1.5 }} onClick={() => { stopRecording(); setListening(false); setParsing(false); }}>
            {t('payables.cancel', 'Cancel')}
          </Button>
        </Box>
      </Fade>

      <Dialog open={sheetOpen} onClose={() => setSheetOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography fontWeight={800}>{t('voice.parsedAs', 'Parsed as')}</Typography>
            <Chip size="small" label={INTENT_LABEL[intent] || intent} color="primary" />
          </Stack>
          {parsed?.rawText && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              “{parsed.rawText}”
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('voice.kind', 'Kind')}</InputLabel>
              <Select label={t('voice.kind', 'Kind')} value={intent} onChange={setField('intent')}>
                <MenuItem value="transaction">{t('navigation.transactions', 'Transaction')}</MenuItem>
                {hasFeature('pantry') && <MenuItem value="pantry">{t('navigation.pantry', 'Pantry')}</MenuItem>}
                {hasFeature('payables') && <MenuItem value="payable">{t('navigation.payables', 'Payable')}</MenuItem>}
              </Select>
            </FormControl>

            {intent === 'transaction' && (
              <Stack direction="row" spacing={1}>
                <Button fullWidth size="small" variant={form.type === 'expense' ? 'contained' : 'outlined'} color="error" onClick={() => setForm((f) => ({ ...f, type: 'expense' }))}>Expense</Button>
                <Button fullWidth size="small" variant={form.type === 'income' ? 'contained' : 'outlined'} color="success" onClick={() => setForm((f) => ({ ...f, type: 'income' }))}>Income</Button>
              </Stack>
            )}

            {intent !== 'pantry' && (
              <TextField size="small" type="number" label={t('transactions.amount', 'Amount')} value={form.amount} onChange={setField('amount')} inputProps={{ min: 0, step: '0.01' }} />
            )}
            <TextField
              size="small"
              label={intent === 'transaction' ? t('transactions.description', 'Description') : t('payables.name', 'Name')}
              value={intent === 'transaction' ? form.description : form.name}
              onChange={intent === 'transaction' ? setField('description') : setField('name')}
            />
            {intent === 'transaction' && form.type === 'expense' && (
              <FormControl fullWidth size="small">
                <InputLabel>{t('transactions.category', 'Category')}</InputLabel>
                <Select label={t('transactions.category', 'Category')} value={form.categoryId || ''} onChange={setField('categoryId')}>
                  {(categories || []).map((c) => (
                    <MenuItem key={c.id || c._id} value={c.id || c._id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {intent === 'transaction' && (
              <TextField size="small" type="date" label={t('transactions.date', 'Date')} value={form.date} onChange={setField('date')} InputLabelProps={{ shrink: true }} />
            )}
            {intent === 'pantry' && (
              <Stack direction="row" spacing={1}>
                <Button fullWidth size="small" variant={form.status === 'need' ? 'contained' : 'outlined'} onClick={() => setForm((f) => ({ ...f, status: 'need', action: 'need' }))}>{t('pantry.need', 'Need')}</Button>
                <Button fullWidth size="small" variant={form.status === 'have' ? 'contained' : 'outlined'} onClick={() => setForm((f) => ({ ...f, status: 'have', action: 'have' }))}>{t('pantry.have', 'Have')}</Button>
              </Stack>
            )}
            {intent === 'payable' && (
              <TextField size="small" type="number" label={t('payables.dueDay', 'Due day')} value={form.dueDay} onChange={setField('dueDay')} inputProps={{ min: 1, max: 31 }} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSheetOpen(false); setTimeout(start, 250); }} startIcon={<ReplayIcon />}>
            {t('voice.tryAgain', 'Try again')}
          </Button>
          <Button variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />} disabled={!canSave || saving} onClick={save}>
            {t('voice.saveTransaction', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3200} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
